// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-SUPPORT-3 R3: Monitoring — the support tier's service-health page.
 * Live tiles (30s refresh): API, DB CPU, sessions, blocked requests, DB size,
 * open/breached tickets — plus jump-offs to Latency, System logs, and the
 * Platform overview. Fleet-wide CPU/DTU per resource arrives with D-SAAS-OBS-1;
 * the banner says so instead of pretending.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Activity, Cpu, Database, Users, ShieldAlert, Inbox, ExternalLink } from 'lucide-react'
import * as api from '../../api/client'
import { useAutoRefresh } from '../../components/useAutoRefresh'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState } from '../../components/workflow/StatePills'

export default function SupportMonitorPage() {
  const [data, setData] = useState<api.SupportMonitor | null>(null)
  const [error, setError] = useState<string | null>(null)

  const alive = useRef(true)
  const pull = () => api.getSupportMonitor()
    .then(d => { if (alive.current) setData(d) })
    .catch(e => { if (alive.current) setError(friendlyError(e)) })
  useEffect(() => { alive.current = true; void pull(); return () => { alive.current = false } }, [])   // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(pull, { intervalMs: 30_000, leaderKey: 'support.monitor' })

  return (
    <div style={{ padding: '24px 24px 48px', maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', width: '100%' }}>
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Monitoring</h1>
        <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginTop: 4 }}>
          Service health at a glance · refreshes every 30 seconds
          {data && <> · last check {new Date(data.checked_at).toLocaleTimeString()}</>}
        </div>
      </header>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
      {!data ? <LoadingState label="Checking service health…" /> : (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <Tile icon={<Activity size={15} />} label="API" value={data.api.status === 'healthy' ? 'Healthy' : data.api.status}
                  tone={data.api.status === 'healthy' ? 'ok' : 'bad'} />
            <Tile icon={<Cpu size={15} />} label="DB CPU"
                  value={data.db.cpu_percent == null ? 'n/a' : `${Math.round(data.db.cpu_percent)}%`}
                  tone={data.db.cpu_percent == null ? undefined : data.db.cpu_percent >= 85 ? 'bad' : data.db.cpu_percent >= 60 ? 'warn' : 'ok'} />
            <Tile icon={<Users size={15} />} label="Active sessions" value={String(data.db.active_sessions)} />
            <Tile icon={<ShieldAlert size={15} />} label="Blocked requests" value={String(data.db.blocked_requests)}
                  tone={data.db.blocked_requests > 0 ? 'warn' : 'ok'} />
            <Tile icon={<Database size={15} />} label="DB size" value={`${(data.db.size_mb / 1024).toFixed(1)} GB`} />
            <Tile icon={<Inbox size={15} />} label="Open tickets" value={String(data.support.open_tickets)} />
            <Tile icon={<ShieldAlert size={15} />} label="SLA breach-flagged" value={String(data.support.breach_flagged)}
                  tone={data.support.breach_flagged > 0 ? 'bad' : 'ok'} />
          </div>

          <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: 'var(--color-text-mute)' }}>
            Per-pharmacy CPU / DTU / memory across the fleet requires Azure Monitor ingestion
            (D-SAAS-OBS-1, in design). Until it ships, deep resource metrics live in the Azure portal — ask Mario.
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Jump to="/admin/latency" label="Latency board" desc="Grid-load and screen latency per pharmacy" />
            <Jump to="/admin/logs" label="System logs" desc="PHI-free structured events — filter by level=error" />
            <Jump to="/admin" label="Platform overview" desc="Overview · Logs & errors · Cost · Storage · Reporting" />
          </div>
        </>
      )}
    </div>
  )
}

function Tile({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: string; tone?: 'ok' | 'warn' | 'bad' }) {
  const color = tone === 'bad' ? '#b91c1c' : tone === 'warn' ? '#b45309' : tone === 'ok' ? '#15803d' : 'var(--color-text)'
  return (
    <div style={{ flex: '1 1 150px', minWidth: 130, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--color-text-mute)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>{icon}{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2, color }}>{value}</div>
    </div>
  )
}

function Jump({ to, label, desc }: { to: string; label: string; desc: string }) {
  return (
    <Link to={to} style={{ flex: '1 1 240px', minWidth: 220, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '12px 14px', textDecoration: 'none', color: 'var(--color-text)' }}>
      <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>{label} <ExternalLink size={13} /></div>
      <div style={{ fontSize: 12.5, color: 'var(--color-text-mute)', marginTop: 3 }}>{desc}</div>
    </Link>
  )
}
